/**
 * Shared time-window options used by the overview throughput chart, the storage
 * growth chart, and the job-state mix. `hours: null` means "all time".
 *
 * `bucketMs` is the aggregation granularity for time-bucketed charts; it is
 * chosen per window so a chart never renders thousands of buckets (e.g. 1h is
 * bucketed every 5 minutes, all-time every week).
 */
export type TimeframeKey = '1h' | '6h' | '24h' | '7d' | '30d' | 'all';

export interface Timeframe {
  key: TimeframeKey;
  label: string;
  /** Lookback window in hours; null = all time (no lower bound). */
  hours: number | null;
  /** Bucket width in milliseconds for time-series aggregation. */
  bucketMs: number;
}

const MINUTE = 60_000;
const HOUR = 3_600_000;
const DAY = 86_400_000;

export const TIMEFRAMES: readonly Timeframe[] = [
  { key: '1h', label: 'Last 1h', hours: 1, bucketMs: 5 * MINUTE },
  { key: '6h', label: 'Last 6h', hours: 6, bucketMs: HOUR },
  { key: '24h', label: 'Last 24h', hours: 24, bucketMs: HOUR },
  { key: '7d', label: 'Last 7d', hours: 24 * 7, bucketMs: DAY },
  { key: '30d', label: 'Last 30d', hours: 24 * 30, bucketMs: DAY },
  { key: 'all', label: 'All time', hours: null, bucketMs: 7 * DAY },
] as const;

export const DEFAULT_TIMEFRAME: TimeframeKey = '24h';

export function getTimeframe(key: TimeframeKey): Timeframe {
  return TIMEFRAMES.find((t) => t.key === key) ?? TIMEFRAMES[2];
}
